// frontend/src/hooks/api/useProviderServicesQueries.ts
// React Query hooks for provider services management

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  providerServicesService,
  ProviderService,
  ProviderServiceFormData,
  ProviderServiceUpdateData,
  ProviderServiceFilters,
  QuickAddServiceData
} from '@/lib/api/providerServicesService';
import { tokenManager } from '@/lib/httpClient';
import { toast } from '../utils/toast';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const providerServicesKeys = {
  all: ['provider-services'] as const,
  lists: () => [...providerServicesKeys.all, 'list'] as const,
  list: (filters: ProviderServiceFilters) => [...providerServicesKeys.lists(), filters] as const,
  details: () => [...providerServicesKeys.all, 'detail'] as const,
  detail: (id: number) => [...providerServicesKeys.details(), id] as const,
  browse: () => [...providerServicesKeys.all, 'browse'] as const,
  browseWithParams: (params: any) => [...providerServicesKeys.browse(), params] as const,
  filterOptions: () => [...providerServicesKeys.all, 'filter-options'] as const,
  stats: () => [...providerServicesKeys.all, 'stats'] as const,
  statsWithParams: (params: any) => [...providerServicesKeys.stats(), params] as const,
} as const;

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Get provider services list with filters
 */
export const useProviderServices = (filters: ProviderServiceFilters = {}) => {
  return useQuery({
    queryKey: providerServicesKeys.list(filters),
    queryFn: async () => {
      const response = await providerServicesService.getServices(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch services');
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Get single provider service by ID
 */
export const useProviderService = (id: number | string) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  
  return useQuery({
    queryKey: providerServicesKeys.detail(numericId),
    queryFn: async () => {
      const response = await providerServicesService.getServiceById(numericId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch service');
    },
    enabled: !!numericId && !isNaN(numericId) && !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Browse available service types for adding
 */
export const useBrowseServiceTypes = (params: {
  search?: string;
  category_id?: number;
  page?: number;
  limit?: number;
} = {}) => {
  return useQuery({
    queryKey: providerServicesKeys.browseWithParams(params),
    queryFn: async () => {
      const response = await providerServicesService.browseServiceTypes(params);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to browse service types');
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 1000, // 5 seconds - for debugging, reduce caching
  });
};

/**
 * Get filter options for services
 */
export const useServiceFilterOptions = () => {
  return useQuery({
    queryKey: providerServicesKeys.filterOptions(),
    queryFn: async () => {
      const response = await providerServicesService.getFilterOptions();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch filter options');
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 10 * 60 * 1000, // 10 minutes - filter options don't change often
  });
};

/**
 * Get service statistics
 */
export const useServiceStats = (params: {
  provider_id?: number;
  date_from?: string;
  date_to?: string;
} = {}) => {
  return useQuery({
    queryKey: providerServicesKeys.statsWithParams(params),
    queryFn: async () => {
      const response = await providerServicesService.getServiceStats(params);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch service statistics');
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 60 * 1000, // 1 minute
  });
};

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create new provider service
 */
export const useCreateProviderService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProviderServiceFormData): Promise<ProviderService> => {
      const response = await providerServicesService.createService(data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to create service');
    },
    onSuccess: (newService) => {
      // Invalidate and refetch services list
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.browse() });
      
      toast.success('Service created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create service');
    },
  });
};

/**
 * Update provider service
 */
export const useUpdateProviderService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ProviderServiceUpdateData }): Promise<ProviderService> => {
      const response = await providerServicesService.updateService(id, data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to update service');
    },
    onSuccess: (updatedService) => {
      // Update specific service in cache
      queryClient.setQueryData(
        providerServicesKeys.detail(updatedService.provider_service_id), 
        updatedService
      );
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      
      toast.success('Service updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update service');
    },
  });
};

/**
 * Delete provider service
 */
export const useDeleteProviderService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<{ message: string }> => {
      const response = await providerServicesService.deleteService(id);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to delete service');
    },
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: providerServicesKeys.detail(deletedId) });
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.browse() });
      
      toast.success('Service deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete service');
    },
  });
};

/**
 * Toggle service status (active/inactive)
 */
export const useToggleServiceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<ProviderService> => {
      const response = await providerServicesService.toggleServiceStatus(id);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to toggle service status');
    },
    onSuccess: (updatedService) => {
      // Update specific service in cache
      queryClient.setQueryData(
        providerServicesKeys.detail(updatedService.provider_service_id), 
        updatedService
      );
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      
      const status = updatedService.is_active ? 'activated' : 'deactivated';
      toast.success(`Service ${status} successfully`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to toggle service status');
    },
  });
};

/**
 * Toggle service featured status
 */
export const useToggleServiceFeatured = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<ProviderService> => {
      const response = await providerServicesService.toggleServiceFeatured(id);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to toggle featured status');
    },
    onSuccess: (updatedService) => {
      // Update specific service in cache
      queryClient.setQueryData(
        providerServicesKeys.detail(updatedService.provider_service_id), 
        updatedService
      );
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      
      const status = updatedService.is_featured ? 'featured' : 'unfeatured';
      toast.success(`Service ${status} successfully`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to toggle featured status');
    },
  });
};

/**
 * Quick add service with minimal data
 */
export const useQuickAddService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: QuickAddServiceData): Promise<ProviderService> => {
      const response = await providerServicesService.quickAddService(data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to quick add service');
    },
    onSuccess: (newService) => {
      // Invalidate and refetch services list
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.browse() });
      
      toast.success('Service added successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to quick add service');
    },
  });
};


/**
 * Approve service (admin only)
 */
export const useApproveService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<ProviderService> => {
      const response = await providerServicesService.approveService(id);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to approve service');
    },
    onSuccess: (updatedService) => {
      // Update specific service in cache
      queryClient.setQueryData(
        providerServicesKeys.detail(updatedService.provider_service_id), 
        updatedService
      );
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      
      toast.success('Service approved successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to approve service');
    },
  });
};

/**
 * Reject service (admin only)
 */
export const useRejectService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason?: string }): Promise<ProviderService> => {
      const response = await providerServicesService.rejectService(id, reason);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to reject service');
    },
    onSuccess: (updatedService) => {
      // Update specific service in cache
      queryClient.setQueryData(
        providerServicesKeys.detail(updatedService.provider_service_id), 
        updatedService
      );
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerServicesKeys.stats() });
      
      toast.success('Service rejected successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reject service');
    },
  });
};

